import { useCallback, useEffect, useRef, useState } from 'react';

export type RecorderState = 'idle' | 'recording' | 'stopping';

export function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
  }
  return '';
}

export function useMicRecorder() {
  const [state, setState] = useState<RecorderState>('idle');
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const resolveStop = useRef<((b: Blob) => void) | null>(null);

  const start = useCallback(async () => {
    if (state !== 'idle') return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
    });
    streamRef.current = stream;
    const mime = pickMimeType();
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      recorderRef.current = null;
      setState('idle');
      resolveStop.current?.(blob);
      resolveStop.current = null;
    };
    recorderRef.current = recorder;
    recorder.start(250);
    setState('recording');
  }, [state]);

  const stop = useCallback((): Promise<Blob> => {
    return new Promise<Blob>((resolve) => {
      const r = recorderRef.current;
      if (!r) return resolve(new Blob());
      resolveStop.current = resolve;
      setState('stopping');
      r.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    const r = recorderRef.current;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    chunksRef.current = [];
    if (r && r.state !== 'inactive') {
      try {
        r.onstop = null;
        r.stop();
      } catch {}
    }
    recorderRef.current = null;
    resolveStop.current = null;
    setState('idle');
  }, []);

  // Cleanup on unmount: release the mic stream so iOS doesn't keep red dot.
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      try { recorderRef.current?.stop(); } catch {}
      recorderRef.current = null;
      resolveStop.current = null;
    };
  }, []);

  return { state, start, stop, cancel };
}
